import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import gql from 'graphql-tag';

import {
  FundraisingPageFragment,
  PageStatus,
} from '../../../types/__generated__';
import config from '../../client/config';
import {
  AboutCampaignAndCharitySection,
  EventDetails,
  FeatureImage,
  PageOwnerTitle,
  PageSection,
  Reason,
  Story,
  TotaliserWrapper,
  Totals,
} from '../../components';
import AboutCauseMini from '../../components/AboutCauseMini/AboutCauseMini';
import { Banner } from '../../components/Banner/Banner';
import DonationSummary from '../../components/DonationSummary/DonationSummary';
import Editbar from '../../components/Editbar/Editbar';
import EncourageInactivityModal from '../../components/EncourageInactivityModal/EncourageInactivityModal';
import FundraisingCTA from '../../components/FundraisingCTA/FundraisingCTA';
import FundraisingHelmet from '../../components/Helmet/FundraisingHelmet';
import IsPageOwner from '../../components/IsPageOwner/IsPageOwner';
import IsQRCode from '../../components/IsQRCode/IsQRCode';
import DefaultLayout from '../../components/Layout/DefaultLayout';
import Page from '../../components/Page/Page';
import PageAlert from '../../components/PageAlert/PageAlert';
import PageOwnerAvatar from '../../components/PageOwnerAvatar/PageOwnerAvatar';
import Prompt from '../../components/Prompt/Prompt';
import QRModal from '../../components/QRModal/QRModal';
import { QualarooLoader } from '../../components/QualarooLoader/QualarooLoader';
import FundraisingSocialButtons from '../../components/SocialButtons/FundraisingSocialButtons';
import StickySummary from '../../components/StickySummary/StickySummary';
import { Strava } from '../../components/Strava';
import SupportersList from '../../components/SupportersList/SupportersList';
import TeamAndCompany from '../../components/TeamAndCompany/TeamAndCompany';
import TeamDetails from '../../components/TeamDetails/TeamDetails';
import TimelinePageSection from '../../components/Timeline/TimelinePageSection/TimelinePageSection';
import { PageMetaContext } from '../../context/PageMetaContext';
import { useNotNullableContext } from '../../hooks/useNotNullableContext';
import usePageQuery, { buildQuery } from '../../hooks/usePageQuery';
import { ActivityTotals } from '../../components/ActivityTotals/ActivityTotals';

import './FundraisingPage.scss';
import styles from './FundraisingPage.scss';

const FundraisingPageQuery = buildQuery(gql`
  fragment FundraisingPage on Page {
    title
    eventGivingGroupId
    owner {
      name
    }
    relationships {
      beneficiaries(first: 1) {
        totalCount
        nodes {
          ... on Charity {
            name
          }
        }
      }
      companies(first: 1) {
        totalCount
      }
      teams(first: 1) {
        nodes {
          status
        }
      }
    }
  }
`);

export const FundraisingPage: React.FunctionComponent = () => {
  const ctaRef = useRef<HTMLDivElement>(null);
  const result = usePageQuery<FundraisingPageFragment>(FundraisingPageQuery);
  const data = result.data;
  const { t } = useTranslation('common', { useSuspense: false });
  const { currentUrl, pageShortName } = useNotNullableContext(PageMetaContext);

  const name = data?.owner?.name;
  const eventGivingGroupId = data && data.eventGivingGroupId;

  const displayTeamAndCompany =
    data?.relationships?.companies.totalCount ||
    data?.relationships?.teams.nodes[0]?.status === PageStatus.Active;

  return (
    <Page>
      <IsQRCode>
        <QRModal ownerName={name ?? ''} />
      </IsQRCode>
      <PageAlert />
      <Banner />
      {eventGivingGroupId && (
        <IsPageOwner>
          <Prompt entityId={eventGivingGroupId.toString()} />
        </IsPageOwner>
      )}
      <Editbar
        adminLink={`${config.baseUri}/account/your-pages/${pageShortName}?pageId=${eventGivingGroupId}`}
      />
      <FundraisingHelmet />
      <DefaultLayout
        slots={{
          featureImage: <FeatureImage enableLightbox />,
          ownerAvatar: <PageOwnerAvatar />,
          titles: <PageOwnerTitle />,
          totals: (
            <>
              <TotaliserWrapper />
              <Totals />
            </>
          ),
          callsToAction: (
            <div ref={ctaRef} className={styles.ctaWrapper}>
              <FundraisingCTA shareUrl={currentUrl} />
            </div>
          ),
          titlesSupplementary: (
            <div className="jg-space-mbxs jg-space-mbsm@xl jg-space-mbn jg-space-mtxs jg-space-mtsm@xl">
              <EventDetails />
              <Reason />
              <TeamDetails />
            </div>
          ),
          causeMini: <AboutCauseMini />,
          activityTotals: <ActivityTotals />,
          main: (
            <>
              <PageSection
                title={t('story.title')}
                className="jg-pages-fundraising__story"
                hasBorder={true}
              >
                <Story />
                <h3 className="jg-text--center">
                  {t('story.shareTitle', 'Share this story')}
                </h3>
                <FundraisingSocialButtons />
              </PageSection>
              <IsPageOwner>
                <PageSection>
                  <Strava />
                </PageSection>
              </IsPageOwner>
              <TimelinePageSection />
            </>
          ),
          aside: (
            <SupportersList
              noSupportersMessage={t(
                'fundraising.noSupportersMessage',
                `Become {{name}}'s first supporter by making a donation`,

                { replace: { name } },
              )}
              displayDonateButton
            />
          ),
          teamAndCompany: displayTeamAndCompany && <TeamAndCompany />,
          contentFooterPrimary: <AboutCampaignAndCharitySection />,
          contentFooterSecondary: (
            <DonationSummary
              fieldsToShow={['onlineAmount', 'offlineAmount', 'textDonations']}
            />
          ),
        }}
      />
      <StickySummary ref={ctaRef} />
      <EncourageInactivityModal />
      <script
        src="//rum-static.pingdom.net/pa-5e3af8dc229e9300080003d1.js"
        async
      ></script>
      <QualarooLoader />
    </Page>
  );
};

FundraisingPage.displayName = 'FundraisingPage';

export default FundraisingPage;
